// ------------------------------------------------------------------------------
// EmailLogin
// author: mudas
// created: 2025/3/30
// ------------------------------------------------------------------------------

import React, { FC, useEffect } from 'react';
import { useResponsive } from 'ahooks';
import { LoginPayload, useLoginHelper } from 'common/hooks';
import { getEnv, judgeIsDanluDomain } from 'common/utils';
import * as Sentry from '@sentry/browser';
import { TiangongThemeType } from 'common/types';
import { LoginProtocolList } from 'common/constants';

import cx from './index.module.css';

export interface IProps {
  // 当前使用的主题类型
  theme?: TiangongThemeType;
  onSuccess?: (value: LoginPayload) => void;
}

const URL_PLACEHOLDER = '请输入邮箱地址';
const PWD_PLACEHOLDER = '请输入密码';

const EmailLogin: FC<IProps> = ({ theme, onSuccess }) => {
  const responsive = useResponsive();

  const { loading, emailLogin } = useLoginHelper({ onSuccess });

  // --------------------------------------------------------------------------
  //
  // Class methods
  //
  // --------------------------------------------------------------------------

  const themeCssFileName = theme === 'light' ? 'customLightTheme' : 'customDarkTheme';
  const cssFiles = `${themeCssFileName}.css,customLogin.css`;

  const init = () => {
    try {
      const { PROD: isProd } = getEnv();
      const host = isProd ? `${window.location.origin}` : `${window.location.origin}/public`;
      const origin = window.location.origin;
      const ele = document.getElementById('urs-login');
      if (ele?.childElementCount) {
        ele.innerHTML = '';
      }
      const isDanlu = judgeIsDanluDomain();

      new URS({
        newCDN: 1, // 必须，代表使用的CDN是连通性更好的SNI域名
        version: 4, // 必须，代表版本4
        cookieDomain: isDanlu ? 'netease.com' : '163.com', // 产品页面地址非163.com域名的必填，可填入的域名列表：https://urs.hz.netease.com/docDetail.html?pid=384#/
        regCookieDomain: isDanlu ? 'netease.com' : '163.com', // 产品页面地址非163.com域名的必填，可填入的域名列表：https://urs.hz.netease.com/docDetail.html?pid=384#/
        cssDomain: `${host}/`,
        cssFiles,
        includeBox: 'urs-login', // 必须，登录框的外层节点的id
        product: 'fuxizhongbao', // 必填，申请的产品名，对应工单生成的配置product
        promark: 'wPyZSiF', // 必填，申请的组件ID，被分派ID，对应工单生成的配置promark
        host: isDanlu ? 'danlu.netease.com' : 'tiangong.yl.163.com', // 必填，产品域名，对应工单生成的配置host
        page: 'login', // 可选，首屏显示登录，可选register
        single: 1, // 可选，只接入登录或注册模块
        isHttps: 1,
        // goEmailLoginTxt: '登录',
        loginText: '登 录',
        placeholder: { account: URL_PLACEHOLDER, pwd: PWD_PLACEHOLDER },
        domainSuffixs: '@163.com,@126.com,@yeah.net,@188.com,@vip.163.com,@vip.126.com',
        setMailloginClause: 1,
        gotoRegText: '没有账号？立即注册',
        forgetPwdText: '忘记密码？',
        mailloginclause: {
          list: LoginProtocolList,
        },
        agreeText: '我已阅读并同意',
        andText: '和',
        logincb: emailLogin,
      });
    } catch (error) {
      console.error('EmailLogin:', error);
      Sentry.captureException(error);
    }
  };

  // --------------------------------------------------------------------------
  //
  // Event handlers
  //
  // --------------------------------------------------------------------------

  // --------------------------------------------------------------------------
  //
  // Effect handlers
  //
  // --------------------------------------------------------------------------

  useEffect(() => {
    init();
  }, [theme]);

  return (
    <div className={cx('mail')}>
      <div id="urs-login" className={cx('urs-login', responsive.xl && 'urs-login-large')}></div>
    </div>
  );
};

export default EmailLogin;
